import React, { useState, useMemo } from 'react';
import { PHASES, CATEGORIES, INITIAL_TASKS } from './constants';
import { PhaseStepper } from './components/PhaseStepper';
import { Sidebar } from './components/Sidebar';
import { TaskItem } from './components/TaskItem';
import { ActionModal } from './components/ActionModal';
import { AddTaskModal } from './components/AddTaskModal';
import { AttachmentManager } from './components/AttachmentManager';
import { Task, TaskStatus, TaskType } from './types';
import { PlusCircle, Search, RefreshCw } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { getProjectHQStagesApi, getProjectHQTasksApi, updateProjectHQTaskStatusApi, updateProjectHQTaskCompletedAtApi } from '@/services/apis';
import { useRequest } from 'ahooks';

const App: React.FC = () => {
    const params = useParams()
    const projectID = params.projectID || 0
    const [currentStage, setCurrentStage] = useState<API.PROJECTHQ.Stage>();
    const [currentStageType, setCurrentStageType] = useState<API.PROJECTHQ.StageType>();
    // const [tasks, setTasks] = useState<Task[]>(INITIAL_TASKS);

    // Modal States
    const [isActionModalOpen, setIsActionModalOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isAttachmentManagerOpen, setIsAttachmentManagerOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState<API.PROJECTHQ.TypeTask | null>(null);

    const { data: stages, loading: stagesLoading, run: fetchStages, refresh: refreshStages } = useRequest(getProjectHQStagesApi, {
        defaultParams: [projectID],
        onSuccess: (res) => {
            if (res.length > 0) {
                let stageID = currentStage?.ID || res[0].ID;
                let newCurrentStage = res.find(stage => stage.ID === stageID);
                setCurrentStage(newCurrentStage);
                if (newCurrentStage && newCurrentStage.ID && newCurrentStage.projectStageTypes.length > 0) {
                    let stageTypeID = currentStageType?.ID || newCurrentStage.projectStageTypes[0].ID;
                    let newCurrentStageType = newCurrentStage.projectStageTypes.find(type => type.ID === stageTypeID);
                    if (newCurrentStageType) {
                        setCurrentStageType(newCurrentStageType);
                        fetchTasks(newCurrentStageType.ID);
                    }
                }
            }
        }
    });

    const { data: tasks, run: fetchTasks } = useRequest(getProjectHQTasksApi, {
        manual: true,
    });



    // Filter Tasks
    const filteredTasks = useMemo(() => {
        return []
        // return tasks.filter(
        //     (task) => task.phaseId === currentPhaseId && task.categoryId === currentCategoryId
        // );
    }, [tasks]);


    // Handlers
    const handleToggleManual = (taskId: string) => {
        // setTasks(prev => prev.map(t => {
        //     if (t.id === taskId && t.type === TaskType.MANUAL) {
        //         return {
        //             ...t,
        //             status: t.status === TaskStatus.COMPLETED ? TaskStatus.PENDING : TaskStatus.COMPLETED
        //         };
        //     }
        //     return t;
        // }));
    };

    const handleActionClick = (task: API.PROJECTHQ.TypeTask) => {
        setSelectedTask(task);
        setIsActionModalOpen(true);
    };

    const handleAttachmentManagerOpen = (task: API.PROJECTHQ.TypeTask) => {
        setSelectedTask(task);
        setIsAttachmentManagerOpen(true);
    };

    const handleStatusChange = async (task: API.PROJECTHQ.TypeTask, status: number) => {
        try {
            await updateProjectHQTaskStatusApi(task.ID, status);
            // 刷新任务列表
            if (currentStageType?.ID) {
                refreshStages();
            }
        } catch (error) {
            console.error('更新状态失败:', error);
        }
    };

    const handleCompletedAtChange = async (task: API.PROJECTHQ.TypeTask, completedAt: string | null) => {
        try {
            await updateProjectHQTaskCompletedAtApi(task.ID, completedAt || '');
            // 刷新任务列表
            if (currentStageType?.ID) {
                fetchTasks(currentStageType.ID);
            }
        } catch (error) {
            console.error('更新完成时间失败:', error);
        }
    };

    

    const handleActionConfirm = (task: API.PROJECTHQ.TypeTask) => {
        // setTasks(prev => prev.map(t => {
        //     if (t.ID === task.ID) {
        //         return { ...t, status: TaskStatus.COMPLETED };
        //     }
        //     return t;
        // }));
    };

    const handleAddTask = (title: string, type: TaskType, dueDate: string, description: string) => {
        // const newTask: Task = {
        //     id: `new_${Date.now()}`,
        //     phaseId: currentPhaseId,
        //     categoryId: currentCategoryId,
        //     title,
        //     type,
        //     status: TaskStatus.PENDING,
        //     dueDate: dueDate || undefined,
        //     description,
        //     isCustom: true
        // };
        // setTasks(prev => [...prev, newTask]);
    };

    const handleSimulateSystemSync = () => {
        // Simulate system tasks updating randomly
        // const hasSystemTasks = tasks.some(t => t.type === TaskType.SYSTEM && t.phaseId === currentPhaseId);
        // if (hasSystemTasks) {
        //     alert("正在从PLM系统同步最新数据...");
        //     setTimeout(() => {
        //         setTasks(prev => prev.map(t => {
        //             if (t.type === TaskType.SYSTEM && Math.random() > 0.5) {
        //                 return { ...t, status: TaskStatus.COMPLETED };
        //             }
        //             return t;
        //         }));
        //         alert("同步完成！");
        //     }, 1500);
        // } else {
        //     alert("当前阶段无系统任务可同步。");
        // }
    };

    const onChangePhase = (phase: API.PROJECTHQ.Stage) => {
        setCurrentStage(phase);
        setCurrentStageType(phase.projectStageTypes[0]);
        fetchTasks(phase.projectStageTypes[0].ID);
    }
    
    if((!stages || stages.length === 0) && !stagesLoading) {
        return <div className="flex items-center justify-center h-full text-gray-400">暂无数据</div>;
    }

    return (
        <div className="h-[calc(100vh-140px)] min-w-min relative flex flex-col">
            {/* 1. Top Navigation - Phases */}
            <div className='shrink-0'>
                <PhaseStepper
                    phases={stages || []}
                    currentPhase={currentStage}
                    onPhaseChange={onChangePhase}
                />
            </div>
           

            <div className="flex flex-1 overflow-y-auto scrollbar-thin">
                {/* 2. Left Sidebar - Categories */}
                <Sidebar
                    stageTypes={currentStage?.projectStageTypes || []}
                    currentStageType={currentStageType}
                    onStageTypeChange={(stageType) => {
                        setCurrentStageType(stageType);
                        fetchTasks(stageType.ID);
                    }}
                />

                {/* 3. Main Content - Task List */}
                <div className="flex-1 bg-blue-100 pr-4 py-4 overflow-y-auto border-none">
                    <div className="p-6 md:p-8 bg-blue-50 min-h-full">
                        <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className='flex items-center'>
                                {/* <div className="text-xl font-bold text-gray-800">{currentStageType?.name}</div> */}
                                <div className="text-xs text-gray-500 ml-2">
                                    当前共有 <span className="font-bold">{currentStageType?.totalCount || 0}</span> 项职责，其中未完成的有 <span className="font-bold">{currentStageType?.uncompletedCount || 0}</span> 项
                                </div>
                            </div>
                        </div>

                        {/* Task Board */}
                        <div className="bg-transparent rounded-xl">
                            <div className="grid grid-cols-1 gap-0">
                                {/* Header Row for Desktop */}
                                <div className="flex gap-2 px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                    <div className="flex-1">任务名称</div>
                                    <div className="w-[100px] text-center">状态</div>
                                    {/* <div className="w-[100px] text-center">完成时间/截止</div> */}
                                    <div className="w-[120px] text-center">操作</div>
                                </div>

                                {tasks && tasks.length > 0 ? (
                                    tasks.map(task => (
                                        <TaskItem
                                            key={task.ID}
                                            task={task}
                                            onToggleManual={handleToggleManual}
                                            onActionClick={handleActionClick}
                                            handleAttachmentManagerOpen={handleAttachmentManagerOpen}
                                            onStatusChange={handleStatusChange}
                                            onCompletedAtChange={handleCompletedAtChange}
                                        />
                                    ))
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-20 bg-white rounded-lg border border-dashed border-gray-300">
                                        <Search size={48} className="text-gray-200 mb-4" />
                                        <p className="text-gray-500 font-medium">当前分类下暂无职责任务</p>
                                        <p className="text-gray-400 text-sm mt-1">请切换分类查看或新增自定义任务</p>
                                    </div>
                                )}

                                {/* Add Placeholder lines to mimic the screenshot's "Future placeholders" */}
                                {filteredTasks.length > 0 && filteredTasks.length < 6 && Array.from({ length: 3 }).map((_, i) => (
                                    <div key={`placeholder-${i}`} className="p-4 mb-3 rounded-lg border border-dashed border-gray-200 bg-gray-50/50 flex items-center">
                                        <div className="w-full text-gray-300 text-sm italic">预留位置 {i + 1} 实际不显示</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    
                </div>
                
            </div>

            {/* Modals */}
            <ActionModal
                task={selectedTask}
                isOpen={isActionModalOpen}
                onClose={() => setIsActionModalOpen(false)}
                onConfirm={handleActionConfirm}
            />

            <AddTaskModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onAdd={handleAddTask}
            />

            <AttachmentManager
                isOpen={isAttachmentManagerOpen}
                task={selectedTask}
                onClose={() => setIsAttachmentManagerOpen(false)}
            />
        </div>
    );
};

export default App;